import React from 'react';

import { type CellContext } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { ALL_POINTS_OPTION } from 'src/features/memberships/utils/constants';
import { membershipMessages } from 'src/features/memberships/utils/messages';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

import type { Point } from 'src/features/points/types';

interface CellAllowedPointsProps<TData, TValue> extends CellContext<TData, TValue> {
    points?: Point[];
    isLoadingPoints?: boolean;
}

function CellAllowedPoints<TData, TValue extends string[]>({
    cell,
    isLoadingPoints,
    points,
}: CellAllowedPointsProps<TData, TValue>) {
    if (isLoadingPoints) return <Skeleton className="h-4 w-24" />;

    const pointIds: string[] = cell.getValue();
    const { formatMessage } = useIntl();

    const containsAllPoints = React.useMemo(() => {
        if (!pointIds || !points) return false;
        return points.map((point) => point.id).every((pointId) => pointIds.includes(pointId));
    }, [pointIds, points]);

    const values = pointIds.map((pointId) => {
        const foundPoint = points?.find((point) => point.id === pointId);
        const isAllPoint = pointId === ALL_POINTS_OPTION;
        return isAllPoint ? formatMessage(membershipMessages.allPoints) : foundPoint?.name || pointId;
    });

    return (
        <Tooltip>
            <TooltipProvider>
                <TooltipTrigger className="max-w-32 truncate">
                    {containsAllPoints ? formatMessage(membershipMessages.allPoints) : values.join(', ')}
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-wrap">
                    {containsAllPoints ? formatMessage(membershipMessages.allPoints) : values.join(', ')}
                </TooltipContent>
            </TooltipProvider>
        </Tooltip>
    );
}

export { CellAllowedPoints };
